// Ported from horizontal-actions-dashboard/frontend/src/components/Header.tsx.
// The source used react-query's useRefresh() mutation; this app has no react-query,
// so refresh state (pending/success/error) is tracked locally with useState.

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Tab, Tabs, Typography, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { refresh as refreshApi } from '../../api/horizontalActionsApi';

const TERMINAL_TABS = [
  { path: '/terminals-summary', label: 'Summary' },
  { path: '/terminals-overview', label: 'Terminal Overview' },
  { path: '/terminals-action-register', label: 'Action Register' },
];

export function TerminalsHeader({ title, onRefreshed }: { title: string; onRefreshed?: () => void }) {
  const [isPending, setIsPending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = TERMINAL_TABS.findIndex(tab => tab.path === location.pathname);

  const handleRefresh = async () => {
    setIsPending(true);
    setStatus('idle');
    try {
      await refreshApi();
      setStatus('success');
      onRefreshed?.();
    } catch {
      setStatus('error');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 2,
        borderBottom: '1px solid #e1e5e9',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a2b3c' }}>
          {title}
        </Typography>
        <Tabs
          value={activeTab === -1 ? false : activeTab}
          onChange={(_, index: number) => navigate(TERMINAL_TABS[index].path)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              textTransform: 'none',
              fontWeight: 500,
              color: '#4a5a6a',
              '&.Mui-selected': { color: '#0067b3', fontWeight: 600 },
            },
            '& .MuiTabs-indicator': { backgroundColor: '#0067b3', height: 2 },
          }}
        >
          {TERMINAL_TABS.map(tab => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
        onClick={handleRefresh}
        disabled={isPending}
      >
        {isPending ? 'Refreshing…' : 'Refresh data'}
      </Button>
      <Box aria-live="polite" sx={{ position: 'absolute', left: -9999 }}>
        {status === 'success' && 'Data refreshed successfully.'}
        {status === 'error' && 'Data refresh failed.'}
      </Box>
    </Box>
  );
}
